import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Loader2, Trash2, Pencil, ShieldCheck, X, Key, UserCog, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import MenuPreview from './MenuPreview';

const EMPTY = { name: '', email: '', initial_password: '', permissions: {} };

// Labels use the sidebar's own module names so each switch is easy to find.
const PERM_LABELS = {
  can_manage_customers:            'Customers & Feedback',
  can_manage_appointments:         'Appointments (online bookings + walk-ins)',
  can_manage_inspections:          'Inspections',
  can_manage_estimates:            'Estimates (Non GST)',
  can_manage_inventory:            'Inventory & Vendor Management',
  can_manage_billing_settings:     'Settings, Labour Chart & GST Setup',
  can_view_expenses:               'Expenses — view only',
  can_manage_expenses:             'Expenses — create / edit / delete',
  can_manage_staff:                'Staff Master & attendance',
  can_self_mark_attendance:        'My Attendance (mark own attendance & OT)',
  can_view_payroll:                'Payroll — view & compute monthly run',
  can_post_payroll:                'Payroll — post to expenses',
  can_manage_performance:          'Performance Management System — manage cycles, goals, final approval',
  can_review_team_performance:     'Performance Management System — evaluate own reportees',
  can_view_reports:                'Reports module (P&L, expense trend, stock & purchasing reports, GST Returns)',
  can_manage_marketplace:          'Pre-owned Marketplace — Cars & Leads',
  can_manage_used_car_inspections: 'Pre-owned Marketplace — Used Car Inspections',
  can_view_activity_log:           'Activity Log',
  can_manage_invoices:             'GST Invoicing & eWarranty — Full access',
  can_view_invoices:               'GST Invoicing & eWarranty — View only',
};

const PERM_SECTIONS = [
  ['Daily work', ['can_manage_customers', 'can_manage_appointments', 'can_manage_inspections', 'can_manage_estimates']],
  ['Stock & purchasing', ['can_manage_inventory']],
  ['Money', ['can_view_expenses', 'can_manage_expenses']],
  ['Team Management', ['can_manage_staff', 'can_self_mark_attendance', 'can_view_payroll', 'can_post_payroll']],
  ['Performance', ['can_manage_performance', 'can_review_team_performance']],
  ['Reports & admin', ['can_view_reports', 'can_manage_billing_settings', 'can_view_activity_log']],
  ['Pre-owned Marketplace', ['can_manage_marketplace', 'can_manage_used_car_inspections']],
];

// GST Invoicing & eWarranty is one three-way choice, not two checkboxes.
const INVOICE_LEVELS = [
  { id: 'none', label: 'No access',
    hint: 'GST Invoices, HSN/SAC Master and eWarranties stay hidden' },
  { id: 'view', label: 'View only',
    hint: 'can look at invoices and warranties and print them, cannot create or send' },
  { id: 'full', label: 'Full access',
    hint: 'can create, edit, send, record payment and issue warranty certificates' },
];
const INVOICE_KEYS = ['can_manage_invoices', 'can_view_invoices'];

const sectionsFor = (keys) => {
  const listed = new Set(PERM_SECTIONS.flatMap(([, ks]) => ks));
  const out = PERM_SECTIONS.map(([t, ks]) => [t, ks.filter((k) => keys.includes(k))]).filter(([, ks]) => ks.length);
  const rest = keys.filter((k) => !listed.has(k) && !INVOICE_KEYS.includes(k));
  return rest.length ? [...out, ['Other', rest]] : out;
};

const invoiceLevelOf = (perms = {}) => {
  if (perms.can_manage_invoices) return 'full';
  if (perms.can_view_invoices) return 'view';
  return 'none';
};

const InvoiceLevelPicker = ({ permissions, onChange }) => {
  const level = invoiceLevelOf(permissions);
  const active = INVOICE_LEVELS.find((l) => l.id === level);
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="invoice-level-picker">
      <div className="text-sm text-slate-800 font-medium">GST Invoicing &amp; eWarranty</div>
      <div className="text-[10px] text-slate-400 font-mono mb-2">
        GST Invoices · HSN/SAC Master · eWarranties
      </div>
      <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
        {INVOICE_LEVELS.map((l) => (
          <button
            key={l.id} type="button" onClick={() => onChange(l.id)}
            className={`text-xs px-3 py-1.5 transition ${level === l.id
              ? 'bg-teal-500 text-white font-semibold'
              : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid={`invoice-level-${l.id}`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">{active?.hint}</p>
      <p className="text-[11px] text-slate-400 mt-1">
        Deleting a GST invoice or a warranty is the master admin&apos;s alone.
      </p>
    </div>
  );
};

const AdminUsersPage = () => {
  const { authHeader, user: me } = useAuth();
  const [list, setList] = useState([]);
  const [keys, setKeys] = useState(Object.keys(PERM_LABELS));
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: ls }, { data: kk }] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: authHeader() }),
        axios.get(`${API}/admin/permission-keys`, { headers: authHeader() }),
      ]);
      setList(ls || []);
      setKeys(kk?.keys || Object.keys(PERM_LABELS));
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    // For staff users we only push the `permissions` field; for admin/manager
    // users we send the full editable payload (name + permissions + optional
    // password reset).
    if (editing.role === 'staff') {
      setSaving(true); setErr('');
      try {
        await axios.put(
          `${API}/admin/users/${editing.id}`,
          { permissions: editing.permissions || {} },
          { headers: authHeader() },
        );
        setEditing(null);
        await fetchAll();
      } catch (e) { setErr(formatErr(e)); }
      finally { setSaving(false); }
      return;
    }
    if (!editing.name || !editing.email) { setErr('Name + email required.'); return; }
    if (!editing.id && (!editing.initial_password || editing.initial_password.length < 8)) {
      setErr('Initial password ≥8 chars required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (editing.id) {
        const { id: _id, email: _e, initial_password: _p, created_at: _c,
          role: _r, staff_id: _s, staff_level: _sl, staff_status: _ss,
          staff_role_label: _srl, joining_date: _jd, ...payload } = editing;
        await axios.put(`${API}/admin/users/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/admin/users`, editing, { headers: authHeader() });
      }
      setEditing(null);
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    if (u.is_master_admin) { alert('Cannot delete the master admin.'); return; }
    if (u.role === 'staff') {
      alert('Staff members must be deleted from the Staff Master page.');
      return;
    }
    if (!window.confirm(`Delete admin user ${u.email}? Their activity log entries are retained.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
  };

  const doReset = async () => {
    if (!resetting.password || resetting.password.length < 8) { alert('≥8 chars'); return; }
    try {
      await axios.put(`${API}/admin/users/${resetting.id}`, { reset_password: resetting.password }, { headers: authHeader() });
      setResetting(null);
      alert('Password reset. Share the new password with the user.');
    } catch (e) { notifyErr(e); }
  };

  const togglePerm = (k) => {
    setEditing((p) => ({ ...p, permissions: { ...(p.permissions || {}), [k]: !(p.permissions || {})[k] } }));
  };

  const setInvoiceLevel = (level) => {
    setEditing((p) => ({
      ...p,
      permissions: {
        ...(p.permissions || {}),
        can_manage_invoices: level === 'full',
        can_view_invoices: level !== 'none',
      },
    }));
  };

  if (!me?.is_master_admin) {
    return <div className="p-6 text-slate-500 text-sm">Master admin only.</div>;
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Admin Users</h1>
        </div>
        <button onClick={() => { setErr(''); setEditing({ ...EMPTY }); }} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-admin-btn">
          <Plus size={14} /> Add Admin User
        </button>
      </div>

      <p className="text-xs text-slate-500">Master Admin (you) can create additional admin/manager users with custom permission toggles, and can also grant per-feature permissions to <b>staff portal users</b> below. Use the <a href="/admin/staff" className="text-teal-700 underline">Staff &amp; Attendance</a> page to add or edit staff master data (salary, level, password, PII).</p>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No admin users yet. Master admin is shown here once seeded.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Name</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Email</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Type</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Permissions</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((u) => {
                const isStaff = u.role === 'staff';
                const activePerms = Object.keys(u.permissions || {}).filter((k) => u.permissions[k]).length;
                return (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`row-${u.id}`}>
                    <td className="px-3 py-2 font-medium">
                      {u.name || '—'}
                      {isStaff && u.staff_level && (
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{u.staff_level} · {u.staff_role_label || 'staff'}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{u.email}</td>
                    <td className="px-3 py-2">
                      {u.is_master_admin ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full"><ShieldCheck size={11} /> Master</span>
                      ) : isStaff ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full" data-testid="badge-staff"><UserCog size={11} /> Staff</span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">Manager</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-slate-500">
                        {u.is_master_admin ? (
                          <em>All permissions</em>
                        ) : activePerms === 0 ? (
                          <em className="text-slate-400">{isStaff ? 'Staff portal only — no admin perms' : 'None — view-only login'}</em>
                        ) : (
                          <span>{activePerms} active</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!u.is_master_admin && (
                        <div className="inline-flex gap-1">
                          {isStaff ? (
                            <>
                              <button
                                onClick={() => { setErr(''); setEditing({ ...u, permissions: u.permissions || {} }); }}
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                                title="Edit permission toggles"
                                data-testid={`edit-${u.id}`}
                              >
                                <Pencil size={14} />
                              </button>
                              <Link
                                to={u.staff_id ? `/admin/staff?focus=${u.staff_id}` : '/admin/staff'}
                                className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                                title="Open in Staff Master"
                                data-testid={`open-staff-${u.id}`}
                              >
                                <ExternalLink size={14} />
                              </Link>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setResetting({ id: u.id, password: '' })} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Reset password" data-testid={`reset-${u.id}`}><Key size={14} /></button>
                              <button onClick={() => { setErr(''); setEditing({ ...u, permissions: u.permissions || {} }); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-${u.id}`}><Pencil size={14} /></button>
                              <button onClick={() => del(u)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`del-${u.id}`}><Trash2 size={14} /></button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">
                {editing.id
                  ? (editing.role === 'staff'
                      ? `Staff Permissions · ${editing.name || editing.email}`
                      : `Edit ${editing.email}`)
                  : 'Add Admin User'}
              </h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-4 overflow-y-auto">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm" data-testid="admin-error">{err}</div>}
              {editing.role === 'staff' ? (
                <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs rounded-lg p-3 flex items-start gap-2">
                  <UserCog size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    Staff master data (name, salary, shift, PII docs, password) is managed from the
                    {' '}<Link to={editing.staff_id ? `/admin/staff?focus=${editing.staff_id}` : '/admin/staff'} className="underline font-semibold">Staff &amp; Attendance</Link>{' '}
                    page. Use the toggles below only to grant this staff member additional <b>admin-console</b> permissions (e.g. view their own payroll page, see inventory reports).
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="admin-name" />
                  <input type="email" placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} disabled={!!editing.id} data-testid="admin-email" />
                  {!editing.id && (
                    <input type="text" placeholder="Initial password (≥8)" value={editing.initial_password || ''} onChange={(e) => setEditing({ ...editing, initial_password: e.target.value })} className={`${inputCls} col-span-2`} data-testid="admin-initial-pw" />
                  )}
                </div>
              )}
              <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
                <div>
                  <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Permissions</h3>
                  <div className="grid grid-cols-1 gap-2 border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="perm-list">
                    <InvoiceLevelPicker
                      permissions={editing.permissions || {}}
                      onChange={setInvoiceLevel}
                    />
                    {sectionsFor(keys).map(([title, ks]) => (
                      <div key={title} data-testid={`perm-section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                        <div className="pt-2 pb-1 px-1.5 text-[10px] uppercase tracking-widest font-semibold text-teal-700">{title}</div>
                        {ks.map((k) => (
                          <label key={k} className="flex items-start gap-2 text-sm cursor-pointer p-1.5 hover:bg-white rounded">
                            <input type="checkbox" checked={!!(editing.permissions || {})[k]} onChange={() => togglePerm(k)} className="mt-0.5" data-testid={`perm-${k}`} />
                            <div className="text-slate-800">{PERM_LABELS[k] || k}</div>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <MenuPreview role={editing.role} permissions={editing.permissions} />
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg" data-testid="admin-save">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {resetting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">Reset Password</h2>
              <button onClick={() => setResetting(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4">
              <input type="text" placeholder="New password (≥8 chars)" value={resetting.password} onChange={(e) => setResetting({ ...resetting, password: e.target.value })} className={inputCls} data-testid="reset-pw" />
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setResetting(null)} className="text-sm px-3 py-2 rounded text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={doReset} className="text-sm bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-2 rounded" data-testid="reset-confirm">Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
